import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';

const DataIndexing = () => {
  const [activeSection, setActiveSection] = useState('pipeline');
  const [progressValues, setProgressValues] = useState({
    documents: 73,
    embeddings: 45,
    bm25: 91
  });

  // Animation des barres de progression au chargement
  useEffect(() => {
    const progressBars = document.querySelectorAll('.progress-fill');
    progressBars.forEach(bar => {
      const width = bar.style.width;
      bar.style.width = '0%';
      setTimeout(() => {
        bar.style.width = width;
      }, 100);
    });
  }, [activeSection]);

  // Animation des cartes au scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });

    const cards = document.querySelectorAll('.stat-card, .source-card, .control-card');
    cards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      card.style.transition = 'all 0.6s ease-out';
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, [activeSection]);

  // Mise à jour périodique des stats
  useEffect(() => {
    const interval = setInterval(() => {
      setProgressValues(prev => ({
        documents: Math.max(0, Math.min(100, prev.documents + (Math.random() - 0.5) * 2)),
        embeddings: Math.max(0, Math.min(100, prev.embeddings + (Math.random() - 0.5) * 2)),
        bm25: Math.max(0, Math.min(100, prev.bm25 + (Math.random() - 0.5) * 2))
      }));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const showSection = (sectionId) => {
    setActiveSection(sectionId);
  };

  const copyCode = (button) => {
    const codeBlock = button.parentElement.nextElementSibling.querySelector('pre');
    const text = codeBlock.textContent;
    
    navigator.clipboard.writeText(text).then(() => {
      const originalText = button.textContent;
      button.textContent = 'Copied!';
      button.style.background = 'rgba(103, 232, 169, 0.3)';
      button.style.color = '#67e8a9';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
        button.style.color = '';
      }, 2000);
    });
  };

  const triggerReindex = (type) => {
    const action = type === 'full' ? 'réindexation complète' : 'synchronisation incrémentale';
    if (window.confirm(`Êtes-vous sûr de vouloir lancer la ${action} ?`)) {
      window.alert(`${action} lancée avec succès. Vous recevrez une notification à la fin.`);
    }
  };

  const cleanupIndex = () => {
    if (window.confirm('Lancer le nettoyage des index ?')) {
      window.alert('Nettoyage des index en cours...');
    }
  };

  const showIndexStats = () => {
    window.alert('Redirection vers le dashboard de statistiques détaillées...');
  };

  const backupIndex = () => {
    if (window.confirm('Créer une sauvegarde des index ?')) {
      window.alert('Sauvegarde lancée. Durée estimée: 15-20 minutes');
    }
  };

  const resetAllIndexes = () => {
    const confirmation = window.prompt('ATTENTION: Cette action supprimera tous les index de façon irréversible. Tapez "RESET" pour confirmer:');
    if (confirmation === 'RESET') {
      window.alert('Reset des index lancé. Le système sera indisponible pendant ~2 heures.');
    }
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Stats Overview */
        .stats-overview {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 2rem;
            margin-bottom: 4rem;
        }
        
        .stat-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            text-align: center;
            transition: all 0.3s;
        }
        
        .stat-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .stat-icon {
            font-size: 2.5rem;
            margin-bottom: 1rem;
        }
        
        .stat-value {
            font-size: 2rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .stat-label {
            color: #888;
            font-size: 0.9rem;
            margin-top: 0.5rem;
        }
        
        /* Section Tabs */
        .section-tabs {
            display: flex;
            justify-content: center;
            gap: 1rem;
            margin-bottom: 3rem;
            flex-wrap: wrap;
        }
        
        .tab-button {
            padding: 0.8rem 2rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            color: #888;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 1rem;
            font-weight: 600;
        }
        
        .tab-button:hover {
            background: rgba(255, 255, 255, 0.1);
            color: white;
        }
        
        .tab-button.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-color: transparent;
        }
        
        /* Content Sections */
        .content-section {
            display: none;
            animation: fadeIn 0.5s ease;
        }
        
        .content-section.active {
            display: block;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        /* Process Flow */
        .process-flow {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            padding: 3rem;
            margin: 3rem 0;
        }
        
        .flow-steps {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 2rem 0;
            flex-wrap: wrap;
            gap: 2rem;
        }
        
        .flow-step {
            flex: 1;
            min-width: 150px;
            text-align: center;
            position: relative;
        }
        
        .flow-step::after {
            content: '→';
            position: absolute;
            right: -1.5rem;
            top: 50%;
            transform: translateY(-50%);
            color: #667eea;
            font-size: 2rem;
            opacity: 0.5;
        }
        
        .flow-step:last-child::after {
            display: none;
        }
        
        .step-icon {
            width: 80px;
            height: 80px;
            margin: 0 auto 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2rem;
            animation: pulse 2s ease-in-out infinite;
        }
        
        @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
        }
        
        .step-title {
            font-weight: bold;
            margin-bottom: 0.5rem;
        }
        
        .step-description {
            font-size: 0.9rem;
            color: #888;
        }
        
        /* Data Source Cards */
        .data-sources {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
            gap: 2rem;
            margin: 3rem 0;
        }
        
        .source-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            transition: all 0.3s;
        }
        
        .source-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .source-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.5rem;
        }
        
        .source-icon {
            width: 50px;
            height: 50px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
        }
        
        .source-info {
            flex: 1;
        }
        
        .source-name {
            font-size: 1.2rem;
            font-weight: bold;
            margin-bottom: 0.3rem;
        }
        
        .source-type {
            color: #667eea;
            font-size: 0.9rem;
        }
        
        .source-stats {
            display: flex;
            justify-content: space-between;
            margin: 1rem 0;
            padding: 1rem;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 8px;
        }
        
        .source-stat {
            text-align: center;
        }
        
        .stat-number {
            font-size: 1.5rem;
            font-weight: bold;
            color: #667eea;
        }
        
        .stat-desc {
            font-size: 0.8rem;
            color: #888;
        }
        
        .source-features {
            list-style: none;
        }
        
        .source-features li {
            padding: 0.4rem 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #bbb;
            font-size: 0.9rem;
        }
        
        .source-features li::before {
            content: '✓';
            color: #67e8a9;
            font-weight: bold;
        }
        
        /* Chunking Strategy */
        .chunking-demo {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 3rem 0;
        }
        
        .chunk-visualization {
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 12px;
            padding: 2rem;
            margin: 2rem 0;
            position: relative;
        }
        
        .chunk-block {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
            border-radius: 8px;
            padding: 1rem;
            margin: 0.5rem 0;
            position: relative;
            transition: all 0.3s;
        }
        
        .chunk-block:hover {
            background: rgba(102, 126, 234, 0.2);
            transform: translateX(10px);
        }
        
        .chunk-header {
            display: flex;
            justify-content: between;
            align-items: center;
            margin-bottom: 0.5rem;
        }
        
        .chunk-id {
            color: #667eea;
            font-weight: bold;
            font-size: 0.9rem;
        }
        
        .chunk-size {
            color: #888;
            font-size: 0.8rem;
        }
        
        .chunk-content {
            color: #ccc;
            font-size: 0.9rem;
            font-family: monospace;
        }
        
        .overlap-indicator {
            position: absolute;
            right: -10px;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(237, 137, 54, 0.8);
            color: white;
            padding: 0.2rem 0.5rem;
            border-radius: 4px;
            font-size: 0.7rem;
            font-weight: bold;
        }
        
        /* Metadata Schema */
        .metadata-schema {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 3rem 0;
        }
        
        .schema-table {
            width: 100%;
            border-collapse: collapse;
            margin: 1.5rem 0;
        }
        
        .schema-table th,
        .schema-table td {
            padding: 1rem;
            text-align: left;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .schema-table th {
            background: rgba(102, 126, 234, 0.1);
            font-weight: bold;
        }
        
        .schema-table tr:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        .field-name {
            font-family: monospace;
            color: #c792ea;
        }
        
        .field-type {
            color: #82aaff;
            font-weight: 600;
        }
        
        .field-example {
            color: #c3e88d;
            font-family: monospace;
            font-size: 0.9rem;
        }
        
        /* Index Management */
        .index-controls {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2rem;
            margin: 3rem 0;
        }
        
        .control-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            text-align: center;
        }
        
        .control-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
        }
        
        .control-title {
            font-size: 1.2rem;
            font-weight: bold;
            margin-bottom: 1rem;
        }
        
        .control-button {
            display: inline-block;
            padding: 0.8rem 2rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-decoration: none;
            border-radius: 8px;
            font-weight: 600;
            transition: all 0.3s;
            border: none;
            cursor: pointer;
            font-size: 1rem;
        }
        
        .control-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        .control-button.secondary {
            background: transparent;
            border: 2px solid #667eea;
            color: #667eea;
        }
        
        .control-button.danger {
            background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
        }
        
        /* Progress Bars */
        .progress-section {
            margin: 2rem 0;
        }
        
        .progress-item {
            margin-bottom: 1.5rem;
        }
        
        .progress-header {
            display: flex;
            justify-content: between;
            align-items: center;
            margin-bottom: 0.5rem;
        }
        
        .progress-label {
            font-weight: 600;
        }
        
        .progress-percentage {
            color: #667eea;
            font-weight: bold;
        }
        
        .progress-bar {
            width: 100%;
            height: 8px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 4px;
            overflow: hidden;
        }
        
        .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
            border-radius: 4px;
            transition: width 0.3s ease;
        }
        
        /* Code Examples */
        .code-example {
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 12px;
            overflow: hidden;
            margin: 2rem 0;
        }
        
        .code-header {
            background: #2a2a2a;
            padding: 1rem 1.5rem;
            display: flex;
            justify-content: between;
            align-items: center;
            border-bottom: 1px solid #333;
        }
        
        .code-title {
            font-weight: 600;
            color: #888;
        }
        
        .copy-button {
            background: rgba(102, 126, 234, 0.2);
            border: 1px solid rgba(102, 126, 234, 0.3);
            color: #667eea;
            padding: 0.4rem 1rem;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 0.9rem;
        }
        
        .copy-button:hover {
            background: rgba(102, 126, 234, 0.3);
        }
        
        .code-content {
            padding: 1.5rem;
            overflow-x: auto;
        }
        
        .code-content pre {
            margin: 0;
            color: #e0e0e0;
            font-family: 'Consolas', 'Monaco', monospace;
            font-size: 0.9rem;
            line-height: 1.6;
        }
        
        /* Alert Boxes */
        .alert-box {
            padding: 1.5rem;
            border-radius: 12px;
            margin: 2rem 0;
            display: flex;
            align-items: start;
            gap: 1rem;
        }
        
        .alert-info {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
        }
        
        .alert-warning {
            background: rgba(237, 137, 54, 0.1);
            border: 1px solid rgba(237, 137, 54, 0.3);
        }
        
        .alert-icon {
            font-size: 1.5rem;
            flex-shrink: 0;
            margin-top: 0.2rem;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
            .stats-overview {
                grid-template-columns: repeat(2, 1fr);
            }
            
            .flow-steps {
                flex-direction: column;
            }
            
            .flow-step::after {
                display: none;
            }
            
            .data-sources {
                grid-template-columns: 1fr;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Indexation et Données</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Indexation et Données</h1>
          <p className="page-subtitle">Gestion et traitement des données pour l'Agent RAG Hybride</p>
        </div>
        
        {/* Stats Overview */}
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-icon">📄</div>
            <div className="stat-value">47,832</div>
            <div className="stat-label">Documents indexés</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🔢</div>
            <div className="stat-value">523,891</div>
            <div className="stat-label">Chunks créés</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🧠</div>
            <div className="stat-value">2.1M</div>
            <div className="stat-label">Embeddings générés</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💾</div>
            <div className="stat-value">89 GB</div>
            <div className="stat-label">Espace utilisé</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-value">98.7%</div>
            <div className="stat-label">Taux de succès</div>
          </div>
        </div>
        
        {/* Section Tabs */}
        <div className="section-tabs">
          <button 
            className={`tab-button ${activeSection === 'pipeline' ? 'active' : ''}`} 
            onClick={() => showSection('pipeline')}
          >
            Pipeline d'Indexation
          </button>
          <button 
            className={`tab-button ${activeSection === 'sources' ? 'active' : ''}`} 
            onClick={() => showSection('sources')}
          >
            Sources de Données
          </button>
          <button 
            className={`tab-button ${activeSection === 'chunking' ? 'active' : ''}`} 
            onClick={() => showSection('chunking')}
          >
            Stratégie de Chunking
          </button>
          <button 
            className={`tab-button ${activeSection === 'metadata' ? 'active' : ''}`} 
            onClick={() => showSection('metadata')}
          >
            Gestion des Métadonnées
          </button>
          <button 
            className={`tab-button ${activeSection === 'management' ? 'active' : ''}`} 
            onClick={() => showSection('management')}
          >
            Gestion des Index
          </button>
        </div>
        
        {/* Pipeline d'Indexation */}
        <div className={`content-section ${activeSection === 'pipeline' ? 'active' : ''}`} id="pipeline">
          <div className="process-flow">
            <h2 style={{textAlign: 'center', marginBottom: '2rem'}}>Pipeline d'Indexation Complète</h2>
            <div className="flow-steps">
              <div className="flow-step">
                <div className="step-icon">📥</div>
                <div className="step-title">Ingestion</div>
                <div className="step-description">Collecte des documents</div>
              </div>
              <div className="flow-step">
                <div className="step-icon">🔍</div>
                <div className="step-title">Extraction</div>
                <div className="step-description">Texte et métadonnées</div>
              </div>
              <div className="flow-step">
                <div className="step-icon">✂️</div>
                <div className="step-title">Chunking</div>
                <div className="step-description">Découpage intelligent</div>
              </div>
              <div className="flow-step">
                <div className="step-icon">🧠</div>
                <div className="step-title">Embeddings</div>
                <div className="step-description">Vectorisation</div>
              </div>
              <div className="flow-step">
                <div className="step-icon">💾</div>
                <div className="step-title">Stockage</div>
                <div className="step-description">Index multi-modalités</div>
              </div>
            </div>
          </div>
          
          {/* Progress Section */}
          <div className="progress-section">
            <h3>État du Pipeline en Temps Réel</h3>
            
            <div className="progress-item">
              <div className="progress-header" style={{display: 'flex', justifyContent: 'space-between'}}>
                <span className="progress-label">📄 Traitement documents en cours</span>
                <span className="progress-percentage">{progressValues.documents.toFixed(0)}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: `${progressValues.documents}%`}}></div>
              </div>
            </div>
            
            <div className="progress-item">
              <div className="progress-header" style={{display: 'flex', justifyContent: 'space-between'}}>
                <span className="progress-label">🧠 Génération embeddings</span>
                <span className="progress-percentage">{progressValues.embeddings.toFixed(0)}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: `${progressValues.embeddings}%`}}></div>
              </div>
            </div>
            
            <div className="progress-item">
              <div className="progress-header" style={{display: 'flex', justifyContent: 'space-between'}}>
                <span className="progress-label">🔍 Indexation BM25</span>
                <span className="progress-percentage">{progressValues.bm25.toFixed(0)}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{width: `${progressValues.bm25}%`}}></div>
              </div>
            </div>
          </div>
          
          {/* Code Example */}
          <div className="code-example">
            <div className="code-header">
              <span className="code-title">Pipeline d'indexation Python</span>
              <button className="copy-button" onClick={(e) => copyCode(e.target)}>Copy</button>
            </div>
            <div className="code-content">
              <pre>{`from rag_pipeline import DocumentProcessor, IndexManager

class IndexingPipeline:
    def __init__(self):
        self.processor = DocumentProcessor()
        self.index_manager = IndexManager()
        
    def process_document_batch(self, documents):
        """Traite un batch de documents"""
        results = []
        
        for doc in documents:
            try:
                # 1. Extraction du contenu
                content = self.processor.extract_content(doc)
                
                # 2. Enrichissement métadonnées
                metadata = self.processor.extract_metadata(doc)
                
                # 3. Chunking intelligent
                chunks = self.processor.create_chunks(
                    content, 
                    chunk_size=1000,
                    overlap=200,
                    preserve_structure=True
                )
                
                # 4. Génération embeddings
                embeddings = self.processor.generate_embeddings(chunks)
                
                # 5. Indexation multi-modalités
                self.index_manager.add_to_vector_index(chunks, embeddings)
                self.index_manager.add_to_bm25_index(chunks)
                self.index_manager.add_to_metadata_index(metadata)
                
                results.append({
                    'document_id': doc.id,
                    'chunks_created': len(chunks),
                    'status': 'success'
                })
                
            except Exception as e:
                results.append({
                    'document_id': doc.id,
                    'error': str(e),
                    'status': 'error'
                })
                
        return results`}</pre>
            </div>
          </div>
        </div>
        
        {/* Sources de Données */}
        <div className={`content-section ${activeSection === 'sources' ? 'active' : ''}`} id="sources">
          <h2 style={{marginBottom: '2rem'}}>Sources de Données Supportées</h2>
          
          <div className="data-sources">
            <div className="source-card">
              <div className="source-header">
                <div className="source-icon">📄</div>
                <div className="source-info">
                  <div className="source-name">Documents PDF</div>
                  <div className="source-type">Procédures & Réglementations</div>
                </div>
              </div>
              <div className="source-stats">
                <div className="source-stat">
                  <div className="stat-number">12,847</div>
                  <div className="stat-desc">Documents</div>
                </div>
                <div className="source-stat">
                  <div className="stat-number">2.3 GB</div>
                  <div className="stat-desc">Taille</div>
                </div>
                <div className="source-stat">
                  <div className="stat-number">99.2%</div>
                  <div className="stat-desc">Succès</div>
                </div>
              </div>
              <ul className="source-features">
                <li>Extraction OCR avec Tesseract</li>
                <li>Détection automatique de la langue</li>
                <li>Préservation de la structure</li>
                <li>Gestion des tableaux complexes</li>
              </ul>
            </div>
            
            <div className="source-card">
              <div className="source-header">
                <div className="source-icon">💻</div>
                <div className="source-info">
                  <div className="source-name">Code Source 4GL</div>
                  <div className="source-type">Modules Applicatifs</div>
                </div>
              </div>
              <div className="source-stats">
                <div className="source-stat">
                  <div className="stat-number">3,521</div>
                  <div className="stat-desc">Modules</div>
                </div>
                <div className="source-stat">
                  <div className="stat-number">487 MB</div>
                  <div className="stat-desc">Taille</div>
                </div>
                <div className="source-stat">
                  <div className="stat-number">97.8%</div>
                  <div className="stat-desc">Succès</div>
                </div>
              </div>
              <ul className="source-features">
                <li>Parser 4GL personnalisé</li>
                <li>Extraction des fonctions et procédures</li>
                <li>Analyse des dépendances</li>
                <li>Documentation automatique</li>
              </ul>
            </div>
            
            <div className="source-card">
              <div className="source-header">
                <div className="source-icon">📊</div>
                <div className="source-info">
                  <div className="source-name">Excel/Word</div>
                  <div className="source-type">Guides Opérationnels</div>
                </div>
              </div>
              <div className="source-stats">
                <div className="source-stat">
                  <div className="stat-number">8,239</div>
                  <div className="stat-desc">Fichiers</div>
                </div>
                <div className="source-stat">
                  <div className="stat-number">1.7 GB</div>
                  <div className="stat-desc">Taille</div>
                </div>
                <div className="source-stat">
                  <div className="stat-number">94.5%</div>
                  <div className="stat-desc">Succès</div>
                </div>
              </div>
              <ul className="source-features">
                <li>Support des macros et formules</li>
                <li>Extraction des diagrammes</li>
                <li>Gestion des versions</li>
                <li>Métadonnées Office complètes</li>
              </ul>
            </div>
            
            <div className="source-card">
              <div className="source-header">
                <div className="source-icon">🌐</div>
                <div className="source-info">
                  <div className="source-name">Confluence/Wiki</div>
                  <div className="source-type">Base de Connaissance</div>
                </div>
              </div>
              <div className="source-stats">
                <div className="source-stat">
                  <div className="stat-number">23,225</div>
                  <div className="stat-desc">Pages</div>
                </div>
                <div className="source-stat">
                  <div className="stat-number">892 MB</div>
                  <div className="stat-desc">Taille</div>
                </div>
                <div className="source-stat">
                  <div className="stat-number">98.9%</div>
                  <div className="stat-desc">Succès</div>
                </div>
              </div>
              <ul className="source-features">
                <li>API REST native</li>
                <li>Synchronisation incrémentale</li>
                <li>Gestion des liens internes</li>
                <li>Historique des versions</li>
              </ul>
            </div>
          </div>
          
          <div className="alert-box alert-info">
            <div className="alert-icon">ℹ️</div>
            <div>
              <h4>Ajout de Nouvelles Sources</h4>
              <p>Le système supporte l'ajout de connecteurs personnalisés. Contactez l'équipe technique pour intégrer de nouveaux types de sources de données.</p>
            </div>
          </div>
        </div>
        
        {/* Stratégie de Chunking */}
        <div className={`content-section ${activeSection === 'chunking' ? 'active' : ''}`} id="chunking">
          <h2 style={{marginBottom: '2rem'}}>Stratégie de Chunking Intelligente</h2>
          
          <div className="chunking-demo">
            <h3>Visualisation du Chunking</h3>
            <p style={{marginBottom: '2rem', color: '#888'}}>
              Découpage adaptatif basé sur la structure du document avec overlap pour préserver le contexte.
            </p>
            
            <div className="chunk-visualization">
              <div className="chunk-block">
                <div className="chunk-header">
                  <span className="chunk-id">Chunk #1</span>
                  <span className="chunk-size">1,024 tokens</span>
                </div>
                <div className="chunk-content">
                  Article 4.2 - Traitement des chèques impayés
                  Selon la directive BEAC n°2023-15, le traitement d'un chèque 
                  impayé doit suivre les étapes suivantes: 1) Vérification du motif...
                </div>
              </div>
              
              <div className="chunk-block">
                <div className="overlap-indicator">Overlap 200</div>
                <div className="chunk-header">
                  <span className="chunk-id">Chunk #2</span>
                  <span className="chunk-size">987 tokens</span>
                </div>
                <div className="chunk-content">
                  ...motif de rejet dans le système, 2) Application des frais selon 
                  le barème en vigueur, 3) Notification client sous 48h ouvrées...
                </div>
              </div>
              
              <div className="chunk-block">
                <div className="overlap-indicator">Overlap 200</div>
                <div className="chunk-header">
                  <span className="chunk-id">Chunk #3</span>
                  <span className="chunk-size">756 tokens</span>
                </div>
                <div className="chunk-content">
                  ...notification doit inclure les informations suivantes: montant, 
                  motif, frais appliqués et démarches de régularisation possibles...
                </div>
              </div>
            </div>
            
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginTop: '2rem'}}>
              <div style={{background: 'rgba(255,255,255,0.05)', padding: '1.5rem', borderRadius: '12px'}}>
                <h4 style={{color: '#667eea', marginBottom: '1rem'}}>⚙️ Configuration</h4>
                <ul style={{listStyle: 'none', color: '#bbb'}}>
                  <li>• Taille: 800-1200 tokens</li>
                  <li>• Overlap: 200 tokens (20%)</li>
                  <li>• Préservation structure</li>
                  <li>• Détection des sections</li>
                </ul>
              </div>
              
              <div style={{background: 'rgba(255,255,255,0.05)', padding: '1.5rem', borderRadius: '12px'}}>
                <h4 style={{color: '#67e8a9', marginBottom: '1rem'}}>✨ Avantages</h4>
                <ul style={{listStyle: 'none', color: '#bbb'}}>
                  <li>• Contexte préservé</li>
                  <li>• Réponses plus précises</li>
                  <li>• Gestion des références</li>
                  <li>• Performance optimisée</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="code-example">
            <div className="code-header">
              <span className="code-title">Algorithme de chunking adaptatif</span>
              <button className="copy-button" onClick={(e) => copyCode(e.target)}>Copy</button>
            </div>
            <div className="code-content">
              <pre>{`class AdaptiveChunker:
    def __init__(self, chunk_size=1000, overlap=200):
        self.chunk_size = chunk_size
        self.overlap = overlap
        self.structure_markers = [
            'Article', 'Section', 'Chapitre', 
            'Procédure', 'Étape', 'Point'
        ]
    
    def chunk_document(self, text, metadata=None):
        """Découpage adaptatif basé sur la structure"""
        chunks = []
        
        # 1. Détection de la structure
        sections = self._detect_sections(text)
        
        # 2. Chunking respectant la structure
        for section in sections:
            section_chunks = self._chunk_section(section)
            chunks.extend(section_chunks)
        
        # 3. Application de l'overlap
        chunks = self._apply_overlap(chunks)
        
        # 4. Enrichissement métadonnées
        chunks = self._enrich_chunks(chunks, metadata)
        
        return chunks
    
    def _detect_sections(self, text):
        """Détecte les sections principales du document"""
        sections = []
        current_section = ""
        
        for line in text.split('\\n'):
            if any(marker in line for marker in self.structure_markers):
                if current_section:
                    sections.append(current_section)
                current_section = line + '\\n'
            else:
                current_section += line + '\\n'
        
        if current_section:
            sections.append(current_section)
            
        return sections`}</pre>
            </div>
          </div>
        </div>
        
        {/* Gestion des Métadonnées */}
        <div className={`content-section ${activeSection === 'metadata' ? 'active' : ''}`} id="metadata">
          <h2 style={{marginBottom: '2rem'}}>Schéma des Métadonnées</h2>
          
          <div className="metadata-schema">
            <h3>Structure des Métadonnées Standardisées</h3>
            
            <table className="schema-table">
              <thead>
                <tr>
                  <th>Champ</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Exemple</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="field-name">document_id</span></td>
                  <td><span className="field-type">UUID</span></td>
                  <td>Identifiant unique du document</td>
                  <td><span className="field-example">550e8400-e29b-41d4...</span></td>
                </tr>
                <tr>
                  <td><span className="field-name">title</span></td>
                  <td><span className="field-type">String</span></td>
                  <td>Titre du document ou section</td>
                  <td><span className="field-example">"Procédure BEAC n°2023-15"</span></td>
                </tr>
                <tr>
                  <td><span className="field-name">document_type</span></td>
                  <td><span className="field-type">Enum</span></td>
                  <td>Type de document</td>
                  <td><span className="field-example">procedure | code | regulation</span></td>
                </tr>
                <tr>
                  <td><span className="field-name">authority</span></td>
                  <td><span className="field-type">String</span></td>
                  <td>Autorité émettrice</td>
                  <td><span className="field-example">"BEAC", "BCEAO", "COBAC"</span></td>
                </tr>
                <tr>
                  <td><span className="field-name">created_date</span></td>
                  <td><span className="field-type">DateTime</span></td>
                  <td>Date de création</td>
                  <td><span className="field-example">2024-01-15T10:30:00Z</span></td>
                </tr>
                <tr>
                  <td><span className="field-name">department</span></td>
                  <td><span className="field-type">String</span></td>
                  <td>Département source</td>
                  <td><span className="field-example">"IT", "Compliance", "Operations"</span></td>
                </tr>
                <tr>
                  <td><span className="field-name">languages</span></td>
                  <td><span className="field-type">Array[String]</span></td>
                  <td>Langues détectées</td>
                  <td><span className="field-example">["fr", "en"]</span></td>
                </tr>
                <tr>
                  <td><span className="field-name">criticality</span></td>
                  <td><span className="field-type">Integer</span></td>
                  <td>Niveau de criticité (1-5)</td>
                  <td><span className="field-example">4</span></td>
                </tr>
                <tr>
                  <td><span className="field-name">tags</span></td>
                  <td><span className="field-type">Array[String]</span></td>
                  <td>Tags métier</td>
                  <td><span className="field-example">["cheque", "impaye", "BEAC"]</span></td>
                </tr>
                <tr>
                  <td><span className="field-name">version</span></td>
                  <td><span className="field-type">String</span></td>
                  <td>Version du document</td>
                  <td><span className="field-example">"v2.1.0"</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="alert-box alert-warning">
            <div className="alert-icon">⚠️</div>
            <div>
              <h4>Enrichissement Automatique</h4>
              <p>Les métadonnées sont automatiquement enrichies par ML : détection de langue, extraction d'entités nommées, classification automatique du type de document.</p>
            </div>
          </div>
        </div>
        
        {/* Gestion des Index */}
        <div className={`content-section ${activeSection === 'management' ? 'active' : ''}`} id="management">
          <h2 style={{marginBottom: '2rem'}}>Gestion des Index</h2>
          
          <div className="index-controls">
            <div className="control-card">
              <div className="control-icon">🔄</div>
              <h3 className="control-title">Réindexation Complète</h3>
              <p style={{marginBottom: '1.5rem', color: '#888'}}>
                Reconstruit tous les index depuis zéro. Opération longue mais garantit la cohérence.
              </p>
              <button className="control-button" onClick={() => triggerReindex('full')}>
                Lancer Réindexation
              </button>
            </div>
            
            <div className="control-card">
              <div className="control-icon">⚡</div>
              <h3 className="control-title">Mise à Jour Incrémentale</h3>
              <p style={{marginBottom: '1.5rem', color: '#888'}}>
                Met à jour uniquement les documents modifiés depuis la dernière synchronisation.
              </p>
              <button className="control-button secondary" onClick={() => triggerReindex('incremental')}>
                Synchronisation
              </button>
            </div>
            
            <div className="control-card">
              <div className="control-icon">🧹</div>
              <h3 className="control-title">Nettoyage Index</h3>
              <p style={{marginBottom: '1.5rem', color: '#888'}}>
                Supprime les documents orphelins et optimise les performances des index.
              </p>
              <button className="control-button secondary" onClick={cleanupIndex}>
                Nettoyer
              </button>
            </div>
            
            <div className="control-card">
              <div className="control-icon">📊</div>
              <h3 className="control-title">Statistiques Index</h3>
              <p style={{marginBottom: '1.5rem', color: '#888'}}>
                Affiche les métriques détaillées de santé et performance des index.
              </p>
              <button className="control-button secondary" onClick={showIndexStats}>
                Voir Stats
              </button>
            </div>
            
            <div className="control-card">
              <div className="control-icon">💾</div>
              <h3 className="control-title">Sauvegarde Index</h3>
              <p style={{marginBottom: '1.5rem', color: '#888'}}>
                Crée une sauvegarde complète des index pour récupération d'urgence.
              </p>
              <button className="control-button secondary" onClick={backupIndex}>
                Sauvegarder
              </button>
            </div>
            
            <div className="control-card">
              <div className="control-icon">🚨</div>
              <h3 className="control-title">Reset Complet</h3>
              <p style={{marginBottom: '1.5rem', color: '#888'}}>
                ⚠️ ATTENTION: Supprime tous les index existants. Action irréversible.
              </p>
              <button className="control-button danger" onClick={resetAllIndexes}>
                Reset Complet
              </button>
            </div>
          </div>
          
          {/* Monitoring Table */}
          <div style={{marginTop: '3rem'}}>
            <h3>État des Index en Temps Réel</h3>
            <table className="schema-table">
              <thead>
                <tr>
                  <th>Index</th>
                  <th>Documents</th>
                  <th>Taille</th>
                  <th>Dernière Maj</th>
                  <th>Statut</th>
                  <th>Performance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Vector Index (FAISS)</strong></td>
                  <td>47,832</td>
                  <td>12.3 GB</td>
                  <td>il y a 2h</td>
                  <td><span style={{color: '#67e8a9'}}>●</span> Healthy</td>
                  <td>~200ms</td>
                </tr>
                <tr>
                  <td><strong>BM25 Index (Elasticsearch)</strong></td>
                  <td>47,832</td>
                  <td>3.7 GB</td>
                  <td>il y a 1h</td>
                  <td><span style={{color: '#67e8a9'}}>●</span> Healthy</td>
                  <td>~50ms</td>
                </tr>
                <tr>
                  <td><strong>Graph Index (Neo4j)</strong></td>
                  <td>15,291 nodes</td>
                  <td>847 MB</td>
                  <td>il y a 3h</td>
                  <td><span style={{color: '#ed8936'}}>●</span> Warning</td>
                  <td>~400ms</td>
                </tr>
                <tr>
                  <td><strong>Metadata Index (MongoDB)</strong></td>
                  <td>47,832</td>
                  <td>234 MB</td>
                  <td>il y a 30min</td>
                  <td><span style={{color: '#67e8a9'}}>●</span> Healthy</td>
                  <td>~25ms</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataIndexing;